/**
 * B2B Checkout Promo Code
 * Validates promo codes via Gadget API (POST /api/checkout/promo-code/validate).
 * If invalid → show error. If valid → store in cart attributes _promo_code .
 */
/* global Theme */
import { getCustomerAppBaseUrl } from "@theme/gadget-envs";

(function () {
  const VALIDATE_URL = `${getCustomerAppBaseUrl()}/api/checkout/promo-code/validate`;
  const PROMO_CODE_ATTR = '_promo_code';

  const input = document.getElementById('b2b-promo-code-input');
  const btn = document.getElementById('b2b-promo-code-apply');
  const errorEl = document.getElementById('b2b-promo-code-error');
  const errorMessageEl = document.getElementById('b2b-promo-code-error-message');
  const successEl = document.getElementById('b2b-promo-code-success');

  if (!input || !btn) return;

  let skipNextInputHide = false;
  let successHideTimer = null;

  function updateButtonState() {
    btn.disabled = !input.value.trim();
  }

  /** Let b2b-checkout.js float-label sync pick up programmatic clears */
  function dispatchInputSync() {
    skipNextInputHide = true;
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  function showError(message) {
    if (errorEl && errorMessageEl) {
      errorMessageEl.textContent = message;
      errorEl.classList.remove('hidden');

      errorEl.closest('.b2b-checkout__field')?.classList.add('b2b-checkout__field--invalid');
    }
    if (successEl) successEl.classList.add('hidden');
  }

  function showSuccess(message) {
    if (successEl) {
      successEl.textContent = message;
      successEl.classList.remove('hidden');

      errorEl?.closest('.b2b-checkout__field')?.classList.remove('b2b-checkout__field--invalid');
    }
    if (errorEl) errorEl.classList.add('hidden');
    if (successHideTimer) clearTimeout(successHideTimer);
    successHideTimer = setTimeout(() => {
      successEl?.classList.add('hidden');
      successHideTimer = null;
    }, 5000);
  }

  function hideMessages() {
    if (errorEl) errorEl.classList.add('hidden');
    if (successEl) successEl.classList.add('hidden');
    input?.closest('.b2b-checkout__field')?.classList.remove('b2b-checkout__field--invalid');
  }

  function getContextFromCookie() {
    const cookies = (document.cookie || '').split(';').reduce((acc, s) => {
      const i = s.indexOf('=');
      if (i === -1) return acc;
      acc[decodeURIComponent(s.slice(0, i).trim())] = decodeURIComponent(s.slice(i + 1).trim());
      return acc;
    }, {});
    try {
      return cookies.context ? JSON.parse(cookies.context) : null;
    } catch {
      return null;
    }
  }

  function getExistingPromoCodesFromCart() {
    return new Promise((resolve) => {
      fetch('/cart.js')
        .then((r) => r.json())
        .then((cart) => {
          const raw = (cart.attributes || {})[PROMO_CODE_ATTR] || '';
          const codes = raw ? raw.split(',').map((c) => c.trim()).filter(Boolean) : [];
          resolve(codes);
        })
        .catch(() => resolve([]));
    });
  }

  async function applyPromoCodeToCartAttribute(codes) {
    const cartRes = await fetch('/cart.js');
    const cart = await cartRes.json();
    const existingAttributes = cart?.attributes || {};
    const value = codes.join(',');
    const cartUpdateUrl = (typeof Theme !== 'undefined' && Theme?.routes?.cart_update_url) || '/cart/update.js';
    const res = await fetch(cartUpdateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        attributes: {
          ...existingAttributes,
          [PROMO_CODE_ATTR]: value,
        }
      })
    });
    return res.json();
  }

  async function getCartProductIds() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      const ids = (cart.items || [])
        .map((item) => item.product_id)
        .filter(Boolean);
      const unique = [...new Set(ids)];
      return unique.map((id) => `gid://shopify/Product/${id}`);
    } catch {
      return [];
    }
  }

  async function applyPromoCode() {
    const code = input.value.trim();
    if (!code) return;

    hideMessages();
    btn.disabled = true;
    const originalText = btn.textContent;
    btn.textContent = btn.dataset.loadingText || 'Applying...';

    const jwt = window.myJWT;
    if (!jwt) {
      showError('Please sign in to apply a promo code.');
      btn.disabled = false;
      btn.textContent = originalText;
      updateButtonState();
      return;
    }

    try {
      const ctx = getContextFromCookie();
      const locationId = ctx?.location_id;
      const companyExternalId = ctx?.company_external_id ?? ctx?.location_external_id ?? ctx?.ship_to_id;

      if (!locationId || !companyExternalId) {
        showError('Company or location context is missing. Please select a company and location.');
        return;
      }

      const companyLocationId = String(locationId).startsWith('gid://')
        ? locationId
        : `gid://shopify/CompanyLocation/${locationId}`;

      const productIds = await getCartProductIds();

      const requestBody = {
        promoCode: code,
        companyLocationId,
        companyExternalId: String(companyExternalId),
        productIds
      };

      // 1. Validate via Gadget API
      const validateRes = await fetch(VALIDATE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwt}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!validateRes.ok) {
        const errData = await validateRes.json().catch(() => ({}));
        const msg = errData?.message || errData?.error || 'Enter a valid discount code.';
        showError(msg);
        return;
      }

      const validateData = await validateRes.json();

      // Only proceed if API explicitly confirms the code is valid (accept both boolean and string)
      const isValid = validateData?.isValid === true || validateData?.isValid === 'true';
      if (!isValid) {
        showError(validateData?.message || validateData?.error || 'Enter a valid discount code.');
        return;
      }

      // Use canonical/transformed code from API if provided, otherwise use input
      const codeToApply = validateData?.code || validateData?.promoCode || code;

      // 2. Apply to cart attribute (merge with existing promo codes)
      const existingCodes = await getExistingPromoCodesFromCart();
      if (existingCodes.some((c) => c.toLowerCase() === codeToApply.toLowerCase())) {
        input.value = '';
        dispatchInputSync();
        showSuccess('This promo code is already applied.');
        return;
      }

      const allCodes = [...existingCodes, codeToApply];
      const cartData = await applyPromoCodeToCartAttribute(allCodes);

      if (cartData?.errors) {
        showError(cartData.errors || 'Could not apply promo code.');
        return;
      }

      input.value = '';
      dispatchInputSync();
      showSuccess('Promo code applied. Discount calculated at invoicing.');

      // Notify other components (e.g. b2b-checkout) to refresh
      document.dispatchEvent(new CustomEvent('discount:update', {
        detail: { resource: cartData },
        bubbles: true
      }));
    } catch {
      showError('An error occurred. Please try again.');
    } finally {
      btn.disabled = false;
      btn.textContent = originalText;
      updateButtonState();
    }
  }

  input.addEventListener('input', () => {
    updateButtonState();
    if (skipNextInputHide) {
      skipNextInputHide = false;
      return;
    }
    hideMessages();
  });
  input.addEventListener('change', updateButtonState);
  input.addEventListener('paste', () => setTimeout(updateButtonState, 0));

  btn.addEventListener('click', (e) => {
    e.preventDefault();
    applyPromoCode();
  });

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (input.value.trim()) applyPromoCode();
    }
  });
})();
